import { Box, Button, LinearProgress, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'

import WizardPointer from './WizardPointer'

interface BookingPageGuidanceProps {
  visible: boolean
  onDismiss: () => void
}

interface GuidanceStep {
  target: string
  content: string
  placement: 'top' | 'bottom' | 'left' | 'right'
}

const guidanceSteps: GuidanceStep[] = [
  {
    target: 'input[type="date"]',
    content: 'Start by selecting the date you want to book a desk',
    placement: 'bottom'
  },
  {
    target: '#office-select',
    content: 'Select an office and then select an available desk below',
    placement: 'bottom'
  },
  {
    target: '[role="button"][aria-label*="desk"]',
    content: 'Click on an available desk to select it for booking',
    placement: 'top'
  }
]

/**
 * BookingPageGuidance - Sequential guidance for booking form
 *
 * Provides step-by-step pointers to guide users through the booking process.
 * Features:
 * - Sequential pointer display
 * - Progress indicator
 * - Skip tutorial button
 * - Auto-advances when user interacts with form
 *
 * Requirements: 12.1, 12.2, 12.3
 */
const BookingPageGuidance: React.FC<BookingPageGuidanceProps> = ({
  visible,
  onDismiss
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [showPointer, setShowPointer] = useState(false)

  // Show pointer after a short delay to ensure DOM is ready
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => {
        setShowPointer(true)
      }, 500)
      return () => clearTimeout(timer)
    } else {
      setShowPointer(false)
      setCurrentStep(0)
    }
    return undefined
  }, [visible])

  // Auto-advance when user interacts with current step's target
  useEffect(() => {
    if (!visible || !showPointer) return undefined

    const currentGuidance = guidanceSteps[currentStep]
    if (!currentGuidance) return undefined

    const targetElement = document.querySelector(currentGuidance.target)
    if (!targetElement) return undefined

    const handleInteraction = () => {
      if (currentStep < guidanceSteps.length - 1) {
        setCurrentStep(prev => prev + 1)
      } else {
        onDismiss()
      }
    }

    // Listen for various interaction events
    targetElement.addEventListener('click', handleInteraction)
    targetElement.addEventListener('change', handleInteraction)
    targetElement.addEventListener('input', handleInteraction)

    return () => {
      targetElement.removeEventListener('click', handleInteraction)
      targetElement.removeEventListener('change', handleInteraction)
      targetElement.removeEventListener('input', handleInteraction)
    }
  }, [visible, showPointer, currentStep, onDismiss])

  const handlePointerDismiss = () => {
    if (currentStep < guidanceSteps.length - 1) {
      setCurrentStep(prev => prev + 1)
    } else {
      onDismiss()
    }
  }

  const handleSkipTutorial = () => {
    onDismiss()
  }

  if (!visible) {
    return null
  }

  const currentGuidance = guidanceSteps[currentStep]
  const progress = ((currentStep + 1) / guidanceSteps.length) * 100

  return (
    <>
      {/* Progress indicator and skip button */}
      <Box
        sx={{
          position: 'fixed',
          top: 80,
          right: 24,
          zIndex: 1400,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          boxShadow: 3,
          p: 2,
          minWidth: 200,
          maxWidth: 280
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1
          }}
        >
          <Typography
            variant='caption'
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            Booking Tutorial
          </Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            {currentStep + 1} / {guidanceSteps.length}
          </Typography>
        </Box>

        <LinearProgress
          variant='determinate'
          value={progress}
          sx={{ mb: 1.5, height: 6, borderRadius: 3 }}
        />

        <Button
          size='small'
          variant='outlined'
          fullWidth
          onClick={handleSkipTutorial}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
        >
          Skip Tutorial
        </Button>
      </Box>

      {/* Current step pointer */}
      {showPointer && currentGuidance && (
        <WizardPointer
          target={currentGuidance.target}
          content={currentGuidance.content}
          placement={currentGuidance.placement}
          visible={true}
          onDismiss={handlePointerDismiss}
        />
      )}
    </>
  )
}

export default BookingPageGuidance
